import { useState, useEffect } from 'react';
import { api } from '../services/authService';

export interface MenuItemType {
  id: number;
  key: string;
  label: string;
  route: string;
  role: string;
  icon: string | null;
  order: number;
  section: string | null;
  is_enabled: boolean;
  is_visible: boolean;
  badge_config: any;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export interface MenuItemsByRole {
  [role: string]: MenuItemType[];
}

/**
 * Hook pentru a obtine meniurile pentru un rol specific
 */
export function useMenuItems(role?: string) {
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setLoading(true);
        setError(null);
        // Pentru utilizatori normali, folosim ruta publica care returneaza meniurile pentru rolul lor
        // Pentru master_admin, folosim ruta de master-admin pentru a vedea toate meniurile
        const endpoint = role === 'master_admin' 
          ? (role ? `/master-admin/menu-items/${role}` : '/master-admin/menu-items')
          : '/auth/menu-items';
        const response = await api.get(endpoint);
        setMenuItems(response.data.data || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Eroare la incarcarea meniurilor');
        // Nu logăm eroarea dacă backend-ul nu rulează
        if (!(err?.message?.includes('Failed to fetch') || err?.message?.includes('Network Error') || err?.code === 'ERR_NETWORK')) {
          console.error('Error fetching menu items:', err);
        }
        // Seteaza array gol pentru a evita folosirea meniurilor hardcodate
        setMenuItems([]);
      } finally {
        setLoading(false);
      }
    };

    if (role) {
      fetchMenuItems();
    } else {
      // Daca nu avem rol specificat, incercam sa obtinem meniurile pentru utilizatorul curent
      fetchMenuItems();
    }
  }, [role]);

  return { menuItems, loading, error, refetch: () => {
    const fetchMenuItems = async () => {
      try {
        setLoading(true);
        setError(null);
        const endpoint = role ? `/master-admin/menu-items/${role}` : '/master-admin/menu-items';
        const response = await api.get(endpoint);
        setMenuItems(response.data.data || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Eroare la incarcarea meniurilor');
      } finally {
        setLoading(false);
      }
    };
    fetchMenuItems();
  }};
}

/**
 * Hook pentru a obtine toate meniurile grupate pe roluri
 */
export function useAllMenuItems() {
  const [menuItemsByRole, setMenuItemsByRole] = useState<MenuItemsByRole>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllMenuItems = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/master-admin/menu-items');
        const items: MenuItemType[] = response.data.data || [];
        
        // Grupeaza pe roluri
        const grouped: MenuItemsByRole = {};
        items.forEach(item => {
          if (!grouped[item.role]) {
            grouped[item.role] = [];
          }
          grouped[item.role].push(item);
        });
        
        // Sorteaza dupa order pentru fiecare rol
        Object.keys(grouped).forEach(role => {
          grouped[role].sort((a, b) => a.order - b.order);
        });
        
        setMenuItemsByRole(grouped);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Eroare la incarcarea meniurilor');
        console.error('Error fetching menu items:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllMenuItems();
  }, []);

  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/master-admin/menu-items');
      const items: MenuItemType[] = response.data.data || [];
      
      const grouped: MenuItemsByRole = {};
      items.forEach(item => {
        if (!grouped[item.role]) {
          grouped[item.role] = [];
        }
        grouped[item.role].push(item);
      });
      
      Object.keys(grouped).forEach(role => {
        grouped[role].sort((a, b) => a.order - b.order);
      });
      
      setMenuItemsByRole(grouped);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la incarcarea meniurilor');
    } finally {
      setLoading(false);
    }
  };

  return { menuItemsByRole, loading, error, refetch };
}


